import { useState } from "react"

/**
 * Renders a document preview that handles both images and PDFs.
 *
 * When `contentType="application/pdf"` is provided, renders <iframe> directly
 * (no broken-image flicker). Otherwise tries <img> first and falls back to
 * <iframe> on load error — keeps the legacy behavior for callers that don't
 * thread the content type through.
 */
export default function DocumentPreview({
  url,
  alt = "Document preview",
  className = "h-[70vh]",
  contentType,
  fitWidth = false,
}: {
  url: string
  alt?: string
  className?: string
  contentType?: string | null
  /**
   * Fit the page to the frame width instead of the default 125% zoom. Right for
   * generated full-page documents (the railcar report) — 125% clips a letter
   * page's right edge. Scanned BOL/COA tickets keep the zoom, where filling the
   * frame matters more than seeing the whole sheet.
   */
  fitWidth?: boolean
}) {
  const [pdfFallbackUrl, setPdfFallbackUrl] = useState<string | null>(null)
  const isPdf = contentType === "application/pdf" || pdfFallbackUrl === url

  if (isPdf) {
    // Adobe-spec PDF URL fragment: zoom=125 starts the browser's built-in
    // viewer at 125% so the document is readable without manual zoom. Toolbar
    // stays so the user can override (zoom in/out, fit-to-page, download).
    const fragmentSeparator = url.includes("#") ? "&" : "#"
    const pdfSrc = `${url}${fragmentSeparator}${fitWidth ? "view=FitH" : "zoom=125"}`
    return (
      <iframe
        src={pdfSrc}
        title={alt}
        className={`w-full rounded border ${className}`}
      />
    )
  }

  return (
    <div className="flex items-center justify-center overflow-auto">
      <img
        src={url}
        alt={alt}
        className={`max-w-full object-contain ${className}`}
        onError={() => setPdfFallbackUrl(url)}
      />
    </div>
  )
}
